
/**
 * Bcp Integration Layer
 * Connects SDK Service to Zustand Store
 */
import i18next from 'i18next';
import { toast } from 'sonner';
import { useBcpStore } from '../../store/bcp.store.js';
import { Logger } from '@/lib/app-log.js';
import { createTeamAssociationIntegrationMethods } from './team-association.integration-mixin.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class BcpIntegration {
    constructor(sdkService) {
        this.service = sdkService;
        Object.assign(this, createTeamAssociationIntegrationMethods({
            afterMutate: this._afterBcpAssociationMutation,
        }));
    }

    _patchSelectedBcpIfCurrent(bcpId, data) {
        if (!bcpId || !data) return;
        const store = useBcpStore.getState();
        const current = store.selectedItem;
        const currentId = current?.id || current?._id;
        if (currentId && String(currentId) === String(bcpId)) {
            if (store.setSelectedBcp) store.setSelectedBcp(data);
            else if (store.setCurrentBcp) store.setCurrentBcp(data);
            else if (store.setSelectedItem) store.setSelectedItem(data);
        }
    }

    syncAfterAssociationMutation(bcpId, data) {
        this._patchSelectedBcpIfCurrent(bcpId, data);
    }

    _afterBcpAssociationMutation(bcpId, data) {
        this.syncAfterAssociationMutation(bcpId, data);
    }

    /**
     * Fetch all items and update store
     */
    async fetchBcps(params = {}) {
        const store = useBcpStore.getState();

        if (store.setLoading) store.setLoading(true);
        if (store.clearError) store.clearError();

        try {
            const response = await this.service.getAll(params);

            // Handle various setter naming conventions
            if (store.setItems) {
                store.setItems(response.data || []);
            } else if (store.setBcps) {
                store.setBcps(response.data || []);
            }

            // [CRITICAL] Handle Pagination Mapping
            const pagination = response.pagination || response.meta;
            if (pagination) {
                const currentPage = parseInt(pagination.page || pagination.currentPage || 1);
                const itemsPerPage = parseInt(pagination.pageSize || pagination.itemsPerPage || 10);
                const totalItems = parseInt(pagination.total || pagination.totalItems || 0);
                const totalPages = parseInt(pagination.totalPages || Math.ceil(totalItems / itemsPerPage) || 1);

                store.setPagination({
                    currentPage,
                    itemsPerPage,
                    totalItems,
                    totalPages,
                    hasNextPage: currentPage < totalPages,
                    hasPreviousPage: currentPage > 1
                });
            }

            // Handle Unique Values (if applicable)
            if (response.uniqueValues && store.setUniqueValues) {
                store.setUniqueValues(response.uniqueValues);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (store.setError) store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Bulk Delete items
     */
    async bulkDeleteBcps(ids) {
        const store = useBcpStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            const response = await this.service.batchDelete(ids);
            const count = response.deletedCount;
            const msg = count !== undefined
                ? i18next.t('integrationToasts:itemsDeletedSuccessfully', {
                    count,
                    defaultValue: `${count} items deleted successfully`,
                })
                : i18next.t('integrationToasts:itemsDeleted', { defaultValue: 'Items deleted' });

            toast.success(msg);
            return { success: true, deletedCount: count };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Get by ID
     */
    async fetchBcpById(id) {
        const store = useBcpStore.getState();
        if (store.setLoading) store.setLoading(true);
        if (store.setSelectedItem) store.setSelectedItem(null);

        try {
            const response = await this.service.getById(id);

            // Try common naming for selecting single item
            if (store.setSelectedBcp) {
                store.setSelectedBcp(response.data);
            } else if (store.setCurrentBcp) {
                store.setCurrentBcp(response.data);
            } else if (store.setSelectedItem) {
                store.setSelectedItem(response.data);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Create item
     */
    async createBcp(data) {
        const store = useBcpStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            const response = await this.service.create(data);

            toast.success(i18next.t('integrationToasts:bcpCreated', { defaultValue: 'Bcp created' }));

            // Optional: Close form if store has that action
            if (store.closeForm) store.closeForm();
            if (store.cancelEditing) store.cancelEditing();

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Update item
     */
    async updateBcp(id, data) {
        const store = useBcpStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            const response = await this.service.update(id, data);

            toast.success(i18next.t('integrationToasts:bcpUpdated', { defaultValue: 'Bcp updated' }));

            if (store.closeForm) store.closeForm();
            if (store.cancelEditing) store.cancelEditing();

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Delete item
     */
    async deleteBcp(id) {
        const store = useBcpStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            await this.service.delete(id);

            toast.success(i18next.t('integrationToasts:bcpDeleted', { defaultValue: 'Bcp deleted' }));

            if (store.closeDeleteDialog) store.closeDeleteDialog();

            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    async fetchStats() {
        const store = useBcpStore.getState();
        try {
            if (!this.service.getStats) {
                return { success: false, error: 'getStats not available' };
            }
            const response = await this.service.getStats();
            if (response.success && store.setStats) {
                store.setStats(response.data);
            }
            return { success: true, data: response.data };
        } catch (error) {
            Logger.error('BcpIntegration.fetchStats failed', { error: error?.message });
            return { success: false, error: error.message };
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }

    // --- Document Relations ---
    async getRelatedDocuments(id, params = {}) {
        try {
            const response = await this.service.getRelatedDocuments(id, params);
            return { success: true, data: response.data, pagination: response.pagination };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async addDocuments(id, documentIds) {
        try {
            const response = await this.service.addDocuments(id, documentIds);
            this._afterBcpAssociationMutation(id, response.data);
            toast.success(i18next.t('integrationToasts:documentsLinkedSuccessfully', { defaultValue: 'Documents linked successfully' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async removeDocument(id, documentId) {
        try {
            const response = await this.service.removeDocument(id, documentId);
            this._afterBcpAssociationMutation(id, response.data);
            toast.success(i18next.t('integrationToasts:documentUnlinkedSuccessfully', { defaultValue: 'Document unlinked successfully' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    // --- Relationship: Exercises Methods ---

    async getRelatedExercises(bcpId, params = {}) {
        try {
            const response = await this.service.getRelatedExercises(bcpId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) {
            const message = this._getErrorMessage(error);
            Logger.error('[BCP Integration] getRelatedExercises error:', { error: message });
            return { success: false, data: [], pagination: {}, error: message };
        }
    }

    async addExercises(bcpId, exerciseIds, opts = {}) {
        try {
            const response = await this.service.addExercises(bcpId, exerciseIds);
            if (!opts.silent) {
                toast.success(i18next.t('integrationToasts:exercisesLinkedSuccessfully', { defaultValue: 'Exercises linked successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (!opts.silent) toast.error(message);
            return { success: false, error: message };
        }
    }

    async removeExercise(bcpId, exerciseId) {
        try {
            const response = await this.service.removeExercise(bcpId, exerciseId);
            toast.success(i18next.t('integrationToasts:exerciseUnlinkedSuccessfully', { defaultValue: 'Exercise unlinked successfully' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async getRelatedBias(bcpId, params = {}) {
        try {
            const response = await this.service.getRelatedBias(bcpId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) {
            const message = this._getErrorMessage(error);
            return { success: false, data: [], pagination: {}, error: message };
        }
    }

    async addBias(bcpId, biaIds, opts = {}) {
        try {
            const response = await this.service.addBias(bcpId, biaIds);
            this._afterBcpAssociationMutation(bcpId, response.data);
            if (!opts.silent) {
                toast.success(i18next.t('bcmBcp:messages.biasLinkedSuccess', { defaultValue: 'BIAs linked successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async removeBia(bcpId, biaId, opts = {}) {
        try {
            const response = await this.service.removeBia(bcpId, biaId);
            this._afterBcpAssociationMutation(bcpId, response.data);
            if (!opts.silent) {
                toast.success(i18next.t('bcmBcp:messages.biaUnlinkedSuccess', { defaultValue: 'BIA unlinked successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    // ==========================================
    // IN SCOPE: PROCESSES
    // ==========================================

    async getRelatedProcesses(bcpId, params = {}) {
        try {
            const response = await this.service.getRelatedProcesses(bcpId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) {
            const message = this._getErrorMessage(error);
            Logger.error('[BCP Integration] getRelatedProcesses error:', { error: message });
            return { success: false, data: [], pagination: {}, error: message };
        }
    }

    async addProcesses(bcpId, processIds, opts = {}) {
        try {
            const response = await this.service.addProcesses(bcpId, processIds);
            this._afterBcpAssociationMutation(bcpId, response.data);
            if (!opts.silent) {
                toast.success(i18next.t('integrationToasts:processesLinkedSuccessfully', { defaultValue: 'Processes linked successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async removeProcess(bcpId, processId) {
        try {
            const response = await this.service.removeProcess(bcpId, processId);
            this._afterBcpAssociationMutation(bcpId, response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    // ==========================================
    // IN SCOPE: ASSETS
    // ==========================================

    async getRelatedAssets(bcpId, params = {}) {
        try {
            const response = await this.service.getRelatedAssets(bcpId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) {
            const message = this._getErrorMessage(error);
            Logger.error('[BCP Integration] getRelatedAssets error:', { error: message });
            return { success: false, data: [], pagination: {}, error: message };
        }
    }

    async addAssets(bcpId, assetIds, opts = {}) {
        try {
            const response = await this.service.addAssets(bcpId, assetIds);
            this._afterBcpAssociationMutation(bcpId, response.data);
            if (!opts.silent) {
                toast.success(i18next.t('integrationToasts:assetsLinkedSuccessfully', { defaultValue: 'Assets linked successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async removeAsset(bcpId, assetId) {
        try {
            const response = await this.service.removeAsset(bcpId, assetId);
            this._afterBcpAssociationMutation(bcpId, response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    // ==========================================
    // IN SCOPE: VENDORS
    // ==========================================

    async getRelatedVendors(bcpId, params = {}) {
        try {
            const response = await this.service.getRelatedVendors(bcpId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) {
            const message = this._getErrorMessage(error);
            Logger.error('[BCP Integration] getRelatedVendors error:', { error: message });
            return { success: false, data: [], pagination: {}, error: message };
        }
    }

    async addVendors(bcpId, vendorIds, opts = {}) {
        try {
            const response = await this.service.addVendors(bcpId, vendorIds);
            this._afterBcpAssociationMutation(bcpId, response.data);
            if (!opts.silent) {
                toast.success(i18next.t('integrationToasts:vendorsLinkedSuccessfully', { defaultValue: 'Vendors linked successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async removeVendor(bcpId, vendorId) {
        try {
            const response = await this.service.removeVendor(bcpId, vendorId);
            this._afterBcpAssociationMutation(bcpId, response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    // ==========================================
    // COMMUNICATION CONTACTS
    // ==========================================

    async getRelatedCommunications(bcpId, params = {}) {
        try {
            const response = await this.service.getRelatedCommunications(bcpId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) {
            const message = this._getErrorMessage(error);
            Logger.error('[BCP Integration] getRelatedCommunications error:', { error: message });
            return { success: false, data: [], pagination: {}, error: message };
        }
    }

    async addCommunications(bcpId, communicationIds) {
        try {
            const response = await this.service.addCommunications(bcpId, communicationIds);
            this._afterBcpAssociationMutation(bcpId, response.data);
            toast.success(i18next.t('integrationToasts:communicationsLinkedSuccessfully', { defaultValue: 'Communication contacts linked successfully' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async removeCommunication(bcpId, communicationId) {
        try {
            const response = await this.service.removeCommunication(bcpId, communicationId);
            this._afterBcpAssociationMutation(bcpId, response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    // ==========================================
    // COMMUNICATION PLANS
    // ==========================================

    async getRelatedCommunicationPlans(bcpId, params = {}) {
        try {
            const response = await this.service.getRelatedCommunicationPlans(bcpId, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) {
            const message = this._getErrorMessage(error);
            Logger.error('[BCP Integration] getRelatedCommunicationPlans error:', { error: message });
            return { success: false, data: [], pagination: {}, error: message };
        }
    }

    async addCommunicationPlans(bcpId, planIds) {
        try {
            const response = await this.service.addCommunicationPlans(bcpId, planIds);
            this._afterBcpAssociationMutation(bcpId, response.data);
            toast.success(i18next.t('integrationToasts:communicationPlansLinkedSuccessfully', { defaultValue: 'Communication plans linked successfully' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async removeCommunicationPlan(bcpId, planId) {
        try {
            const response = await this.service.removeCommunicationPlan(bcpId, planId);
            this._afterBcpAssociationMutation(bcpId, response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async fetchBcpParty(bcpId) {
        try {
            const response = await this.service.getParty(bcpId);
            return {
                success: response.success !== false,
                data: response.data,
                partyTemplate: response.partyTemplate ?? null,
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, data: null, partyTemplate: null, error: message };
        }
    }

    async syncBcpParty(bcpId, payload, options = {}) {
        const { silentSuccess = false } = options;
        try {
            const response = await this.service.syncParty(bcpId, payload);
            if (!silentSuccess) {
                toast.success(i18next.t('bcmBcp:messages.partySaveSuccess', { defaultValue: 'Ownership and roles saved' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async submitBcp(bcpId) {
        try {
            const response = await this.service.submit(bcpId);
            toast.success(i18next.t('bcmBcp:messages.submitSuccess', { defaultValue: 'Record submitted for approval' }));
            this._patchSelectedBcpIfCurrent(bcpId, response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }
}
